import { ApiClient } from '../apiClient';
import { getPosthogDistinctId } from '../../lib/posthog-utils';
import type {
  UserSession,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  UserProfile,
  UpdateProfileRequest,
  UpdatePasswordRequest,
  TwoFactorSetupResponse,
  TwoFactorEnableRequest,
  TwoFactorEnableResponse,
  TwoFactorDisableRequest,
  TwoFactorVerifyRequest,
} from '../../types/auth';

export class AuthService {
  constructor(private apiClient: ApiClient) {}

  async login(data: LoginRequest): Promise<LoginResponse> {
    // Same pattern as the cart endpoints: the distinct id lets the backend
    // activity store link this device's anonymous actor to the account.
    const posthogDistinctId = getPosthogDistinctId();
    return this.apiClient.post<LoginResponse>('/user/login', {
      ...data,
      ...(posthogDistinctId && { posthogDistinctId }),
    });
  }

  async register(data: RegisterRequest): Promise<boolean> {
    const posthogDistinctId = getPosthogDistinctId();
    return this.apiClient.post<boolean>('/user/register', {
      ...data,
      ...(posthogDistinctId && { posthogDistinctId }),
    });
  }

  async confirmEmail(token: string): Promise<UserSession> {
    return this.apiClient.post<UserSession>(`/user/confirm-email?token=${encodeURIComponent(token)}`);
  }

  // Disabled 2026-08-25. Nothing called this, and /user/authenticate-token was
  // never a route - the short-lived access token was removed from the backend
  // long ago, leaving the user-cookie as the only credential.
  // async authenticateToken(token: string): Promise<UserSession> {
  //   return this.apiClient.post<UserSession>('/user/authenticate-token', { token });
  // }

  async googleAuth(code: string, referral?: string): Promise<LoginResponse> {
    const params = new URLSearchParams({ code });
    if (referral) params.set('referral', referral);
    return this.apiClient.post<LoginResponse>(`/user/google-auth-callback?${params.toString()}`);
  }

  async facebookAuth(code: string, referral?: string): Promise<LoginResponse> {
    const params = new URLSearchParams({ code });
    if (referral) params.set('referral', referral);
    return this.apiClient.post<LoginResponse>(`/user/facebook-auth-callback?${params.toString()}`);
  }

  async githubAuth(code: string, referral?: string): Promise<LoginResponse> {
    const params = new URLSearchParams({ code });
    if (referral) params.set('referral', referral);
    return this.apiClient.post<LoginResponse>(`/user/github-auth-callback?${params.toString()}`);
  }

  async twitterAuth(code: string, codeVerifier: string, referral?: string): Promise<LoginResponse> {
    const params = new URLSearchParams({ code, code_verifier: codeVerifier });
    if (referral) params.set('referral', referral);
    return this.apiClient.post<LoginResponse>(`/user/twitter-auth-callback?${params.toString()}`);
  }

  // Disabled 2026-08-25 alongside the backend handler: nothing called this,
  // and the refresh happens server-side from the user-cookie on every request.
  // async refreshToken(refreshToken: string): Promise<UserSession> {
  //   return this.apiClient.post<UserSession>('/user/refresh-token', { refreshToken });
  // }

  async validateForgotPasswordToken(token: string): Promise<boolean> {
    return this.apiClient.post<boolean>('/user/authenticate-forgot-password-token', { token });
  }

  async forgotPassword(email: string, locale?: string, turnstileToken?: string): Promise<boolean> {
    return this.apiClient.post<boolean>('/user/forgot-password', { email, locale, turnstileToken });
  }

  async resetPassword(token: string, password: string, turnstileToken?: string): Promise<UserSession> {
    return this.apiClient.post<UserSession>('/user/confirm-forgot-password', { token, password, turnstileToken });
  }

  async logout(): Promise<void> {
    return this.apiClient.get<void>('/user/logout');
  }

  async getProfile(): Promise<UserProfile> {
    return this.apiClient.get<UserProfile>('/user/profile');
  }

  async getUser(): Promise<UserProfile> {
    return this.apiClient.get<UserProfile>('/user');
  }

  async updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    return this.apiClient.patch<UserProfile>('/user/profile', data);
  }

  async updatePassword(data: UpdatePasswordRequest): Promise<{ success: boolean; message: string }> {
    return this.apiClient.patch<{ success: boolean; message: string }>('/user/update-password', data);
  }

  async updateLocale(locale: string): Promise<{ locale: string }> {
    return this.apiClient.patch<{ locale: string }>('/user/locale', { locale });
  }

  async twoFactorSetup(): Promise<TwoFactorSetupResponse> {
    return this.apiClient.post<TwoFactorSetupResponse>('/user/2fa/setup');
  }

  async twoFactorEnable(data: TwoFactorEnableRequest): Promise<TwoFactorEnableResponse> {
    return this.apiClient.post<TwoFactorEnableResponse>('/user/2fa/enable', data);
  }

  async twoFactorDisable(data: TwoFactorDisableRequest): Promise<{ success: boolean; message: string }> {
    return this.apiClient.post<{ success: boolean; message: string }>('/user/2fa/disable', data);
  }

  async twoFactorVerify(data: TwoFactorVerifyRequest): Promise<UserSession> {
    return this.apiClient.post<UserSession>('/user/2fa/verify', data);
  }

  // `code` is the 2FA code (authenticator or backup); needed when 2FA is on.
  async deleteAccount(password: string, code?: string): Promise<{ success: boolean; message: string }> {
    return this.apiClient.deleteWithBody<{ success: boolean; message: string }>('/user/delete-account', {
      password,
      ...(code ? { code } : {}),
    });
  }
}
